import * as React from "react";
import { Content, Portal, Root, Trigger } from "@radix-ui/react-popover";
import { Check, ChevronDown, LayoutGrid, PenLine, Plus, Search } from "lucide-react";

import { cn } from "utils/cn";
import {
  filterProjectsByQuery,
  formatRecentDate,
  formatUpdatedLabel,
  type ProjectDraft,
  type SwitcherProject,
} from "./switcherProjects";

export const projectInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "P";

const MAX_RECENT_PROJECTS = 8;

const sectionLabelClassName =
  "px-2 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground";

const focusRingClassName =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary";

const optionClassName = cn(
  "flex min-h-[34px] w-full items-center gap-2.5 rounded-[7px] px-2 py-1.5 text-left text-[13px] text-popover-foreground transition-colors",
  "hover:bg-muted focus-visible:bg-muted disabled:cursor-not-allowed disabled:opacity-60",
  focusRingClassName
);

const triggerClassName = cn(
  "inline-flex min-w-0 items-center gap-1 rounded-md px-1.5 py-1 text-sm transition-colors",
  "hover:bg-muted aria-expanded:bg-muted",
  focusRingClassName
);

const popoverClassName =
  "z-[200] flex max-h-[min(470px,var(--radix-popover-content-available-height))] w-[300px] max-w-[calc(100vw-24px)] flex-col rounded-[10px] border border-border bg-popover p-1.5 text-popover-foreground shadow-[0_12px_32px_rgb(0_0_0/0.2)] outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0";

/** Up and Down move between options; Home and End jump to the ends. */
const handleListKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  // Home and End keep moving the caret while typing in the search field.
  if (event.target instanceof HTMLInputElement && (event.key === "Home" || event.key === "End")) {
    return;
  }
  const container = event.currentTarget;
  const options = Array.from(
    container.querySelectorAll<HTMLElement>("[data-switcher-option]:not([disabled])")
  );
  if (options.length === 0) return;
  event.preventDefault();
  const currentIndex = options.indexOf(document.activeElement as HTMLElement);
  let nextIndex = 0;
  if (event.key === "End") nextIndex = options.length - 1;
  else if (event.key === "ArrowDown") nextIndex = currentIndex < 0 ? 0 : currentIndex + 1;
  else if (event.key === "ArrowUp")
    nextIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
  options[Math.min(nextIndex, options.length - 1)]?.focus();
};

/** A breadcrumb trigger ("The Lantern" with a chevron) and its popover. */
const SwitcherPopover = ({
  open,
  onOpenChange,
  triggerLabel,
  triggerText,
  triggerClassName: extraTriggerClassName,
  dialogLabel,
  onOpenAutoFocus,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible name of the trigger. */
  triggerLabel: string;
  triggerText: string;
  triggerClassName?: string;
  dialogLabel: string;
  onOpenAutoFocus: (event: Event) => void;
  children: React.ReactNode;
}) => (
  <Root open={open} onOpenChange={onOpenChange}>
    <Trigger asChild>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        className={cn(triggerClassName, extraTriggerClassName)}
      >
        <span className="min-w-0 truncate">{triggerText}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
    </Trigger>
    <Portal>
      <Content
        role="dialog"
        aria-label={dialogLabel}
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        onOpenAutoFocus={onOpenAutoFocus}
        onKeyDown={handleListKeyDown}
        className={popoverClassName}
      >
        {children}
      </Content>
    </Portal>
  </Root>
);

export interface DraftListProps {
  projectName: string;
  drafts: ProjectDraft[];
  currentDraftId: string | null;
  draftsLoading: boolean;
  canSwitchDraft: boolean;
  /** A draft activation is in flight: every draft is disabled until it settles. */
  draftSwitchPending?: boolean;
  onSelectDraft: (draft: ProjectDraft) => void;
}

/** The project's drafts, newest first, with the current one checked. */
const DraftList = ({
  projectName,
  drafts,
  currentDraftId,
  draftsLoading,
  canSwitchDraft,
  draftSwitchPending = false,
  onSelectDraft,
}: DraftListProps) => {
  const labelId = React.useId();
  return (
    <div role="group" aria-labelledby={labelId} aria-busy={draftSwitchPending || undefined}>
      <div id={labelId} className={sectionLabelClassName}>
        Drafts of {projectName}
      </div>
      {draftSwitchPending ? (
        <div role="status" className="px-2 pb-1 text-xs text-muted-foreground">
          Switching drafts…
        </div>
      ) : null}
      {draftsLoading ? (
        <div className="px-2 py-2 text-xs text-muted-foreground">Loading drafts…</div>
      ) : drafts.length === 0 ? (
        <div className="px-2 py-2 text-xs text-muted-foreground">No drafts yet.</div>
      ) : (
        drafts.map((draft) => {
          const isCurrent = draft.documentId === currentDraftId;
          const date = formatRecentDate(draft.createdAt);
          return (
            <button
              key={draft.documentId}
              type="button"
              data-switcher-option
              data-current-draft={isCurrent || undefined}
              disabled={draftSwitchPending || (!canSwitchDraft && !isCurrent)}
              aria-current={isCurrent ? "true" : undefined}
              title={
                !draftSwitchPending && !canSwitchDraft && !isCurrent
                  ? "Only people who can edit this project can switch drafts."
                  : undefined
              }
              onClick={() => onSelectDraft(draft)}
              className={optionClassName}
            >
              <PenLine className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                {draft.label}
                {date ? <span className="text-[11px] text-muted-foreground"> · {date}</span> : null}
              </span>
              {isCurrent ? (
                <Check
                  className="h-4 w-4 shrink-0 text-selected-foreground"
                  aria-label="Current draft"
                />
              ) : null}
            </button>
          );
        })
      )}
    </div>
  );
};

export interface ProjectSwitcherProps {
  projectName: string;
  /** Other projects of the workspace, most recent first. */
  projects: SwitcherProject[];
  canCreateProject: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectProject: (project: SwitcherProject, via: "recent" | "search") => void;
  onAllProjects: () => void;
  onNewProject: () => void;
  /**
   * Also list the drafts above the projects. Used below md, where the
   * header has no room for a separate draft trigger.
   */
  draftList?: DraftListProps;
  /** "Studio", "Writer" and so on. No pill when null. */
  licenseLabel?: string | null;
  /**
   * Show the license pill in the menu's header row instead of next to the
   * trigger. Used below md, where the breadcrumb has no room for it.
   */
  licenseInMenu?: boolean;
}

/** The project's license, as a small muted outline pill. */
export const LicensePill = ({ label }: { label: string }) => (
  <span
    data-license-pill
    className="inline-flex shrink-0 items-center rounded-full border border-chrome-border px-1.5 text-[10px] font-semibold leading-4 text-muted-foreground"
  >
    {label}
  </span>
);

/**
 * The project name in the header breadcrumb, followed by its license pill.
 * Opens a switcher with a search field, recent projects, All projects and
 * New project.
 */
export const ProjectSwitcher = ({
  projectName,
  projects,
  canCreateProject,
  open,
  onOpenChange,
  onSelectProject,
  onAllProjects,
  onNewProject,
  draftList,
  licenseLabel = null,
  licenseInMenu = false,
}: ProjectSwitcherProps) => {
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);
  const labelId = React.useId();
  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const visibleProjects = React.useMemo(() => {
    const matches = filterProjectsByQuery(projects, trimmedQuery);
    return isSearching ? matches : matches.slice(0, MAX_RECENT_PROJECTS);
  }, [isSearching, projects, trimmedQuery]);

  return (
    <>
      <SwitcherPopover
        open={open}
        onOpenChange={onOpenChange}
        triggerLabel={`Switch project. Current: ${projectName}`}
        triggerText={projectName}
        triggerClassName="max-w-[11rem] font-semibold text-foreground md:max-w-[16rem]"
        dialogLabel="Switch project"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchRef.current?.focus();
        }}
      >
        {licenseInMenu && licenseLabel ? (
          <div data-menu-header className="flex min-w-0 items-center gap-2 px-2 pb-1.5 pt-1">
            <span className="min-w-0 truncate text-[13px] font-semibold text-popover-foreground">
              {projectName}
            </span>
            <LicensePill label={licenseLabel} />
          </div>
        ) : null}
        <div className="relative mb-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            ref={searchRef}
            type="text"
            role="searchbox"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search projects"
            aria-label="Search projects"
            autoComplete="off"
            className="w-full rounded-[7px] border border-border bg-background py-[7px] pl-[30px] pr-2 text-[12.5px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {draftList && !isSearching ? <DraftList {...draftList} /> : null}

          <div role="group" aria-labelledby={labelId}>
            <div id={labelId} className={sectionLabelClassName}>
              {isSearching ? "Projects" : "Recent projects"}
            </div>
            {visibleProjects.length === 0 ? (
              <div className="px-2 py-2.5 text-xs text-muted-foreground">
                {isSearching
                  ? `No project matches "${trimmedQuery}".`
                  : "No other projects in this workspace."}
              </div>
            ) : (
              visibleProjects.map((project) => {
                const meta = [project.license, formatUpdatedLabel(project.updatedAt)]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <button
                    key={project.projectId}
                    type="button"
                    data-switcher-option
                    onClick={() => onSelectProject(project, isSearching ? "search" : "recent")}
                    className={optionClassName}
                  >
                    <span
                      aria-hidden
                      className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md bg-muted text-[10px] font-semibold text-foreground"
                    >
                      {projectInitials(project.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{project.name}</span>
                      {meta ? (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {meta}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="mt-1 flex flex-col border-t border-border pt-1">
          <button
            type="button"
            data-switcher-option
            onClick={onAllProjects}
            className={optionClassName}
          >
            <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="flex-1">All projects</span>
          </button>
          {canCreateProject ? (
            <button
              type="button"
              data-switcher-option
              onClick={onNewProject}
              className={optionClassName}
            >
              <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="flex-1">New project</span>
            </button>
          ) : null}
        </div>
      </SwitcherPopover>
      {!licenseInMenu && licenseLabel ? <LicensePill label={licenseLabel} /> : null}
    </>
  );
};

export interface DraftSwitcherProps extends DraftListProps {
  /** "Draft 3" for the draft being viewed. */
  currentDraftLabel: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The draft label in the header breadcrumb. Opens the project's drafts. */
export const DraftSwitcher = ({
  currentDraftLabel,
  open,
  onOpenChange,
  ...draftList
}: DraftSwitcherProps) => {
  const label = currentDraftLabel ?? "Drafts";
  return (
    <SwitcherPopover
      open={open}
      onOpenChange={onOpenChange}
      triggerLabel={
        currentDraftLabel ? `Switch draft. Current: ${currentDraftLabel}` : "Switch draft"
      }
      triggerText={label}
      triggerClassName="max-w-[9rem] font-medium text-muted-foreground hover:text-foreground"
      dialogLabel="Switch draft"
      onOpenAutoFocus={(event) => {
        // Start on the current draft, or the first one that can be picked.
        const content = event.currentTarget as HTMLElement | null;
        const target =
          content?.querySelector<HTMLElement>("[data-current-draft]:not([disabled])") ??
          content?.querySelector<HTMLElement>("[data-switcher-option]:not([disabled])");
        if (!target) return;
        event.preventDefault();
        target.focus();
      }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        <DraftList {...draftList} />
      </div>
    </SwitcherPopover>
  );
};
